// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { RegistrarService } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Registrar';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import Loading from '../../../components/Loading';
import { useLedgerExercise } from '../../../hooks/mutations/ledger/useLedgerExercise';
import useMutate from '../../../hooks/mutations/useMutate';
import useClientOnboarderServices from '../../../hooks/queries/registry/onboarding/useClientOnboarderServices';
import useParticipantAgentBatchOperations from '../../../hooks/queries/registry/onboarding/useParticipantAgentBatchOperations';
import { useParticipantServices } from '../../../hooks/queries/registry/onboarding/useParticipantServices';
import useRegistrarBatchOperations from '../../../hooks/queries/registry/onboarding/useRegistrarBatchOperations';
import useServiceUpgradeConsents from '../../../hooks/queries/registry/onboarding/useServiceUpgradeConsents';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { useGetAllInstrumentConfigurations } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { ERR_OFFBOARD_REGISTRAR, SUC_OFFBOARD_REGISTRAR } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  registrarServiceContractId: string;
  registrar: string;
};

export const OffboardRegistrarDialog = ({
  open,
  onClose,
  registrarServiceContractId,
  registrar,
}: DialogProps) => {
  const mutate = useMutate();
  const terminateRegistrarMutation = useLedgerExercise(RegistrarService.RegistrarService_Terminate);
  const holdings = useHoldings();
  const participantServices = useParticipantServices();
  const clientOnboarderServices = useClientOnboarderServices();
  const instrumentConfigurationsQuery = useGetAllInstrumentConfigurations();
  const serviceUpgradeConsents = useServiceUpgradeConsents();
  const registrarBatchOperations = useRegistrarBatchOperations();
  const participantAgentBatchOperations = useParticipantAgentBatchOperations();

  const registrarHoldings = useMemo(
    () => holdings.data?.filter((h) => h.payload.registrar === registrar) ?? [],
    [holdings.data, registrar],
  );

  const registrarParticipantServices = useMemo(
    () => participantServices.data?.filter((ps) => ps.payload.registrar === registrar) ?? [],
    [participantServices.data, registrar],
  );

  const registrarInstrumentConfigurations = useMemo(
    () =>
      instrumentConfigurationsQuery.data?.instrumentConfigurations.filter(
        (instrumentConfiguration) => instrumentConfiguration.payload.registrar === registrar,
      ) ?? [],
    [instrumentConfigurationsQuery.data, registrar],
  );

  const registrarClientOnboarderServices = useMemo(
    () => clientOnboarderServices.data?.filter((cos) => cos.payload.registrar === registrar) ?? [],
    [clientOnboarderServices.data, registrar],
  );

  const registrarUpgradeConsent = useMemo(
    () => serviceUpgradeConsents.data?.find((c) => c.payload.authorizer === registrar) ?? null,
    [serviceUpgradeConsents.data, registrar],
  );

  const registrarBatchOps = useMemo(
    () => registrarBatchOperations.data?.find((b) => b.payload.registrar === registrar) ?? null,
    [registrarBatchOperations.data, registrar],
  );

  const participantAgentBatchOps = useMemo(
    () =>
      participantAgentBatchOperations.data?.find((b) => b.payload.registrar === registrar) ?? null,
    [participantAgentBatchOperations.data, registrar],
  );

  const hasHoldings = registrarHoldings.length > 0;
  const hasParticipantServices = registrarParticipantServices.length > 0;
  const hasInstrumentConfigurations = registrarInstrumentConfigurations.length > 0;
  const hasClientOnboarderServices = registrarClientOnboarderServices.length > 0;
  const hasBlockingContracts =
    hasHoldings ||
    hasParticipantServices ||
    hasInstrumentConfigurations ||
    hasClientOnboarderServices;

  // Registrars onboarded before the batch-operations contracts were provisioned at onboarding
  // have nothing for `RegistrarService_Terminate` to archive, and it rejects a missing argument.
  const isMissingProvisionedContracts =
    !registrarUpgradeConsent || !registrarBatchOps || !participantAgentBatchOps;

  const offboardRegistrar = useCallback(async () => {
    if (!registrarUpgradeConsent || !registrarBatchOps || !participantAgentBatchOps) return;

    const payload = {
      cid: registrarServiceContractId as ContractId<RegistrarService>,
      argument: {
        registrarUpgrade: registrarUpgradeConsent.contractId,
        registrarBatchOps: registrarBatchOps.contractId,
        participantAgentBatchOps: participantAgentBatchOps.contractId,
      },
    };

    await mutate(
      terminateRegistrarMutation,
      payload,
      SUC_OFFBOARD_REGISTRAR,
      ERR_OFFBOARD_REGISTRAR,
    );
    onClose();
  }, [
    registrarServiceContractId,
    registrarUpgradeConsent,
    registrarBatchOps,
    participantAgentBatchOps,
    onClose,
    mutate,
    terminateRegistrarMutation,
  ]);

  const isLoadingQueries =
    instrumentConfigurationsQuery.isLoading ||
    participantServices.isLoading ||
    clientOnboarderServices.isLoading ||
    holdings.isLoading ||
    serviceUpgradeConsents.isLoading ||
    registrarBatchOperations.isLoading ||
    participantAgentBatchOperations.isLoading;
  const mutationIsPending = terminateRegistrarMutation.isPending;
  const disableSubmit =
    isLoadingQueries || hasBlockingContracts || isMissingProvisionedContracts || mutationIsPending;

  return (
    <FormDialog
      open={open}
      title="Offboard Registrar"
      onCancel={onClose}
      onSubmit={offboardRegistrar}
      submitName="Offboard"
      isSubmitDisabled={disableSubmit}
      submitButtonSxProps={{ color: 'error' }}
    >
      {isLoadingQueries ? (
        <Loading />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {hasHoldings && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Registrars with issued holdings cannot be offboarded.
            </Alert>
          )}
          {hasParticipantServices && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Registrars with active participant services cannot be offboarded.
            </Alert>
          )}
          {hasInstrumentConfigurations && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Registrars with active instrument configurations cannot be offboarded.
            </Alert>
          )}
          {hasClientOnboarderServices && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Registrars with active client onboarder services cannot be offboarded.
            </Alert>
          )}
          {isMissingProvisionedContracts && (
            <Alert severity="error" sx={{ mb: 1 }}>
              This registrar is missing its upgrade consent or batch operations contracts, so it
              cannot be offboarded from here. They must be created before offboarding.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This will terminate the registrar service.
          </Typography>
        </Box>
      )}
    </FormDialog>
  );
};

export default OffboardRegistrarDialog;
